import { Box, List, ListItem, Text } from '@chakra-ui/react';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { InsightBullet, KeyInsightCategory, KeyInsights } from '@resistapp/common/key-insights/types';
import { RefObject } from 'react';
import { theme } from '../../shared/theme';
import { SiteDetailTitle } from '../site-detail-title';
import { SiteDetailContainer } from './general-site-details';
import { detailGraphHeight } from './site-details-utils';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  insights: KeyInsights;
  id?: string;
}

const BulletItem = ({ bullet, level = 0 }: { bullet: InsightBullet; level?: number }) => {
  const { setMonthStable } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();

  const handleBulletClick = (clickedBullet: InsightBullet) => {
    // OVERALL is not clickable by default, but if it were, it would clear selections.
    if (clickedBullet.category === KeyInsightCategory.OVERALL) {
      setMonthStable(null, null); // Clear month
      // Clear targets by toggling an empty array and removing old selections
      queryFilters.toggleGeneGroupStable([], true);
      return;
    }

    // Only proceed if the bullet has a selection action
    if (!clickedBullet.select) return;

    // Default: Clear month selection unless specifically set by the bullet
    setMonthStable(clickedBullet.select.month ?? null, null);

    // Handle ANTIBIOTIC: Select only L2 Target
    if (clickedBullet.category === KeyInsightCategory.ANTIBIOTIC && clickedBullet.select.l2Target) {
      queryFilters.toggleGeneGroupStable([clickedBullet.select.l2Target], true);
      return; // Done for ANTIBIOTIC
    }

    // Handle other selectable bullets (e.g., CHALLENGE sub-bullets)
    if (clickedBullet.select.l2Target) {
      queryFilters.toggleGeneGroupStable([clickedBullet.select.l2Target], true);
    }
    if (clickedBullet.select.assay) {
      queryFilters.toggleSingleTarget(clickedBullet.select.assay);
    }
    if (clickedBullet.select.minorTarget) {
      console.error('Minor target selection not implemented yet', clickedBullet.select.minorTarget);
    }

    // TODO: Potentially add scrollIntoView for the related graph?
  };

  const isClickable = !!bullet.select || bullet.category === KeyInsightCategory.OVERALL;
  const paddingLeft = level * 4;
  const negativeIndent = '-1.2em';

  // Determine if this specific bullet is the *only* selected target
  const { selectedTargets } = queryFilters.filters;
  const isSelected =
    selectedTargets.length === 1 &&
    ((bullet.select?.l2Target && selectedTargets[0] === bullet.select.l2Target) ||
      (bullet.select?.assay && selectedTargets[0] === bullet.select.assay));

  // Determine font weight based on selection and level
  const fontWeight = isSelected ? 'extrabold' : level === 0 ? 'semibold' : 'medium';

  return (
    <Box mb={level === 0 ? 2 : 0}>
      <ListItem
        key={bullet.id}
        onClick={() => {
          if (isClickable) {
            handleBulletClick(bullet);
          }
        }}
        cursor={isClickable ? 'pointer' : 'default'}
        _hover={isClickable ? { bg: theme.colors.neutral50 } : {}}
        borderRadius="md"
        p={0}
        listStyleType={level > 0 ? 'circle' : 'disc'}
        listStylePosition="inside"
        pl={paddingLeft} // Apply overall indent
        textIndent={negativeIndent} // Apply negative indent for hanging effect
      >
        <Text as="span" fontSize="sm" fontWeight={fontWeight}>
          {bullet.text}
        </Text>
      </ListItem>
      {/* Children List: Set spacing to 1, remove pt */}
      {bullet.children && bullet.children.length > 0 && (
        <List styleType="none" spacing={1} pt={0} pl={0}>
          {bullet.children.map(child => (
            <BulletItem key={child.id} bullet={child} level={level + 1} />
          ))}
        </List>
      )}
    </Box>
  );
};

export function SiteKeyInsightsBox({ containerRef, insights, id }: Props) {
  return (
    <SiteDetailContainer ref={containerRef} id={id} height={`${detailGraphHeight}px`} overflowY="auto">
      <SiteDetailTitle title="Key Insights" explanation="Important highlights from the whole monitoring period." />
      <Box pt={2} pl={2} pr={2}>
        {/* Set base spacing for the main list (e.g., 1) */}
        <List spacing={1} styleType="none" pl={4}>
          {insights.bullets.map(bullet => (
            <BulletItem key={bullet.id} bullet={bullet} level={0} />
          ))}
        </List>
      </Box>
    </SiteDetailContainer>
  );
}
